<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="accounts"
      :loading="tableLoading"
      density="comfortable"
      class="elevation-0 ncrm-smtp-accounts-table"
      item-key="id"
      :row-props="getRowProps"
      @click:row="onRowClick"
    >
      <template #item.name="{ item }">
        <div class="font-weight-medium">{{ item.name }}</div>
        <div v-if="item.description" class="text-caption text-medium-emphasis">
          {{ item.description }}
        </div>
      </template>

      <template #item.from_email="{ item }">
        <div class="font-weight-medium">{{ item.from_email }}</div>
        <div v-if="item.from_name" class="text-caption text-medium-emphasis">
          {{ item.from_name }}
        </div>
      </template>

      <template #item.server="{ item }">
        <div class="font-weight-medium">{{ formatServer(item) }}</div>
        <div class="text-caption text-medium-emphasis">
          {{ tlsLabel(item) }}
        </div>
      </template>

      <template #item.status="{ item }">
        <div class="d-flex flex-wrap align-center ga-1">
          <v-chip :color="item.status === 'active' ? 'success' : 'neutral'">
            {{ $t(`ncrm.integrations.smtp.status.${item.status || "active"}`) }}
          </v-chip>
          <v-chip v-if="item.is_default" color="primary">
            {{ $t("ncrm.integrations.smtp.defaultBadge") }}
          </v-chip>
        </div>
      </template>

      <template #item.last_test_status="{ item }">
        <v-chip :color="testStatusColor(item.last_test_status)">
          {{ $t(`ncrm.integrations.smtp.lastTest.${item.last_test_status || "unknown"}`) }}
        </v-chip>
        <span v-if="item.last_tested_at" class="ml-2 text-caption text-medium-emphasis">
          {{ formatDate(item.last_tested_at) }}
        </span>
      </template>

      <template #item.actions="{ item }">
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          :loading="testingId === item.id"
          @click.stop="$emit('test', item)"
        >
          <v-icon start size="16">mdi-lan-connect</v-icon>
          {{ $t("ncrm.integrations.smtp.actions.test") }}
        </v-btn>
        <v-btn
          size="small"
          variant="text"
          class="ml-1"
          :title="$t('ncrm.integrations.smtp.actions.sendTest')"
          @click.stop="$emit('send-test', item)"
        >
          <v-icon size="18">mdi-email-fast-outline</v-icon>
        </v-btn>
        <v-btn
          size="small"
          variant="text"
          class="ml-1"
          :title="$t('ncrm.integrations.smtp.actions.edit')"
          @click.stop="$emit('edit', item)"
        >
          <v-icon size="18">mdi-pencil</v-icon>
        </v-btn>
        <v-btn
          size="small"
          variant="text"
          color="error"
          class="ml-1"
          :title="$t('ncrm.integrations.smtp.actions.delete')"
          @click.stop="$emit('delete', item)"
        >
          <v-icon size="18">mdi-delete-outline</v-icon>
        </v-btn>
      </template>

      <template #no-data>
        <v-alert type="info" class="ma-4">
          {{ $t("ncrm.integrations.smtp.empty") }}
        </v-alert>
      </template>
    </v-data-table>

    <v-card v-if="selectedAccount" class="mt-4" variant="tonal">
      <v-card-text class="d-flex flex-wrap align-center ga-3">
        <div>
          <div class="text-subtitle-2">{{ selectedAccount.name }}</div>
          <div class="text-caption text-medium-emphasis">
            {{ selectedAccount.from_email }} · {{ formatServer(selectedAccount) }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          variant="tonal"
          color="primary"
          :loading="testingId === selectedAccount.id"
          @click="$emit('test', selectedAccount)"
        >
          <v-icon start size="16">mdi-lan-connect</v-icon>
          {{ $t("ncrm.integrations.smtp.actions.testConnection") }}
        </v-btn>
        <v-btn variant="tonal" color="secondary" @click="$emit('send-test', selectedAccount)">
          <v-icon start size="16">mdi-email-fast-outline</v-icon>
          {{ $t("ncrm.integrations.smtp.actions.sendTest") }}
        </v-btn>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";
export default {
  name: "NcrmCompanySmtpAccountsTable",
  props: {
    accounts: {
      type: Array,
      default: () => [],
    },
    tableLoading: {
      type: Boolean,
      default: false,
    },
    testingId: {
      type: [Number, String],
      default: null,
    },
    selectedAccountId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["select", "test", "send-test", "edit", "delete"],
  computed: {
    headers() {
      return [
        { title: this.$t("ncrm.integrations.smtp.headers.name"), key: "name" },
        { title: this.$t("ncrm.integrations.smtp.headers.from"), key: "from_email" },
        { title: this.$t("ncrm.integrations.smtp.headers.server"), key: "server" },
        { title: this.$t("ncrm.integrations.smtp.headers.status"), key: "status" },
        { title: this.$t("ncrm.integrations.smtp.headers.lastTest"), key: "last_test_status" },
        {
          title: this.$t("ncrm.integrations.smtp.headers.actions"),
          key: "actions",
          sortable: false,
          align: "end",
        },
      ];
    },
    selectedAccount() {
      if (!this.selectedAccountId) return null;
      return this.accounts.find((account) => account.id === this.selectedAccountId) || null;
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return "";
      try {
        return formatDisplayDateTime(value);
      } catch (_) {
        return String(value);
      }
    },
    formatServer(account) {
      const host = account?.host || "-";
      const port = account?.port || "-";
      return `${host}:${port}`;
    },
    tlsLabel(account) {
      if (account?.secure) return this.$t("ncrm.integrations.smtp.tls.implicit");
      if (account?.require_tls) return this.$t("ncrm.integrations.smtp.tls.starttls");
      return this.$t("ncrm.integrations.smtp.tls.none");
    },
    testStatusColor(status) {
      if (status === "ok") return "success";
      if (status === "error") return "error";
      return "neutral";
    },
    getRowProps({ item }) {
      if (!item) return {};
      const isSelected = this.selectedAccountId === item.id;
      return {
        class: isSelected ? "ncrm-smtp-row--selected" : "",
        style: { cursor: "pointer" },
      };
    },
    onRowClick(_event, row) {
      const item = row?.item || row;
      if (item?.id) this.$emit("select", item);
    },
  },
};
</script>

<style scoped>
.ncrm-smtp-accounts-table :deep(.ncrm-smtp-row--selected) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}
</style>
